

/*

STILL TO ADD:

Insights
  What’s the most important factor when deciding to buy [product category]?

  If [your brand] ceased to exist, how would you start to look for alternatives?
    How/where do customers look for services like yours?

    How were you accomplishing your goals before you started using us?

    If [your brand] was a person, how would you describe that person?

  Where do you start looking first for services/products like ours?


Competitors:
  When thinking about companies that provide [product category], which one comes to mind first?
  Which company/companies have you used for similar services in the last 2 years?

Growth:
  What other products/services/features would you like to see [your brand] offer?


Pricing:
  At what price would you consider [product category] to be so expensive that you would not consider buying it?
  At what price would you consider [product category] to be priced so low that you would feel the quality couldn’t be very good?
  At what price would you consider [product category] to be a bargain – a great buy for the money?

CONTINUE HERE:
https://www.askattest.com/blog/marketing/100-great-survey-questions

*/

/*
  let format = {
      allBusinesess: [
      {
        groupName: 'Competitor Insights',
        groupDesc: null,
        questions: getCompetitorQuestions()
      }
    ],
    restaurants: [
      {
        groupName: 'Staff Insights',
        groupDesc: null,
        questions: getAllRestaurantQuestions()
      }
    ]
  }
*/

export const getQuestionTemplates = () => {
  return {
    allBusinesess: [
      {
        groupName: 'Insights',
        groupDesc: 'Discover valuable insights you may not have known.',
        questions: insightsQuestions()
      },
      {
        groupName: 'Growth',
        groupDesc: 'Get your customers\' view on where you need to grow.',
        questions: growthQuestions()
      },
      {
        groupName: 'Strengths',
        groupDesc: 'Discover what your business strengths are in fresh perspectives.',
        questions: strengthsInsights()
      },
      {
        groupName: 'Metrics',
        groupDesc: 'Measure customer satisfaction, marketing channels and loyalty.',
        questions: metricQuestions()
      },
      {
        groupName: 'Improvement',
        groupDesc: 'Pinpoint where to improve on existing features.',
        questions: improveQuestions()
      },
      {
        groupName: 'Alternatives/Competitors',
        groupDesc: 'Discover insights into your competitive landscape.',
        questions: competitorQuestions()
      },
      {
        groupName: 'Responsiveness',
        groupDesc: 'Measure how well your business respond to customer queries and communications.',
        questions: responsivenessQuestions()
      }
    ]
  }
}

/*

Growth:



*/

function growthQuestions() {
  return [
    genQuestion(
      'What, if any, would make our offerings more complete?', //text
    ),
    genQuestion(
      'What would you like to be added to our product/service?', //text
    ),
    genQuestion(
      'How can we better support your business/your goals?'
    ),
    genQuestion(
      'If there was one new feature you could suggest, what would it be and why?',
    ),
    genQuestion(
      'Which of these are most important to you?',
      'multiq',
      ['Growth area/feature/product 1', 'Growth area/feature/product 2', 'Growth area/feature/Product 3'],
      'A typical \'Where should we expand\' question, answered by the most important people - your customers. List the growth areas or features that you\'re considering expanding into. This question will signal where you need to focus to grow your business with success.'
    ),
    genQuestion(
      'What are the most important services/features we\'re missing?',
      'multiq',
      ['Growth area/feature/product 1', 'Growth area/feature/product 2', 'Growth area/feature/Product 3'],
      'A variation of the \'Where should we expand\' question. List the growth areas or features that you\'re considering expanding into. This question will signal where you need to focus to grow your business with success.'
    ),
  ]
}

function competitorQuestions() {
  return [
    genQuestion(
      'Why did you choose us over our competitors?',
    ),
    genQuestion(
      'Before choosing us, what alternatives did you consider, if any?',
      'multiq',
      ['Competitor 1', 'Competitor 2', 'Competitor 3'],
      'This question could also be useful as a free-text question.'
    ),
    genQuestion(
      'Compared to our competitors, is our product/service quality better, worse, or about the same?',
      'singleq',
      ['Better', 'Similar', 'Worse']
    )
  ]
}

function insightsQuestions() {
  return [
    genQuestion(
      'What would you say to someone who asked about us?',
      'openq',
      [],
      'Alternatives: \'In your own words, describe how you feel about us.\' or \'How does our product/service make you feel\''
    ),
    genQuestion(
      'Did the description of our products/services match what you received?',
      'singleq',
      ['Yes', 'Somewhat', 'No'],
      'This question helps measure the comprehension and accuracy of your sales & marketing materials.'
    ),
    genQuestion(
      'Off the top of your head, what are 3 words that you’ll use to describe _businessname_?', //text
    ),
    genQuestion(
      'What are the top reasons that made you choose us?', //text
      'multiq', //type 'multiq'/'openq'/'singleq'
      ['Value for money', 'Quality', 'Simplicity', 'Easy reach'],
      'This question could also be useful as a free-text question.'
    ),
    genQuestion(
      'Have you seen or heard about us anywhere else?'
    ),
    genQuestion(
      'What are your business\' biggest challenges?',
      'openq',
      [],
      'A broad question that could potentially mine key information on how you can better serve your customers.'
    ),
    genQuestion(
      'What else would you like us to know?', //text
    )
  ]
}

function strengthsInsights() {
  return [
    genQuestion(
      'What specific needs does our product/service meet for you?' //text
    ),
    genQuestion(
      'What do you like most about our product/service?',
      'openq',
      [],
      'Alternatives: \'What features of our product/service stand out to you?\' or \'What’s one thing we should never stop doing?\'', //text //text
    ),
    genQuestion(
      'What\'s working for you and why?',
      'openq',
      [],
      'Alternatives: \'What challenge or problem does our business solve for you?\' or \'How do our products/services help you achieve your goals?\''
    ),
    genQuestion(
      'How has our product/service made an impact on you/your business/your lifestyle?'
    )
  ]
}

function metricQuestions() {
  return [
    genQuestion(
      'How long have you been a customer?',
      'singleq',
      ['Less than a month','1 to 6 Months', 'Around a Year', '2 Years+'],
      'Measure how long customers stay, and how often you get new ones.'
    ),
    genQuestion(
      'How likely are you to use us again?',
      'singleq',
      ['Very likely', 'Likely', 'Maybe', 'Not likely', 'Very unlikely']
    ),
    genQuestion(
      'How did you find us?',
      'singleq',
      ['Physical ad', 'Web search', 'Friend/colleague', 'Online ad', 'Through a blog'],
    ),
    genQuestion(
      'How likely are you to buy from us again?',
      'singleq',
      ['Very likely', 'Likely' , 'Unlikely', 'Very unlikely']
    ),
    genQuestion(
      'Do you consider yourself a loyal customer of _businessname_?',
      'singleq',
      ['Yes', 'No', 'Maybe'],
      'A simple metric question that gives you an assessment of how many customers consider themselves to be loyal to your business - an indication of the probability of repeat business and brand loyalty.'
    ),
    genQuestion(
      'How often do you typically use our products/services?', //text
      'singleq', //type 'multiq'/'openq'/'singleq'
      ['Every day', 'Once or twice a week', 'Every month', 'Not that often'] //answer options
    ),
    genQuestion(
      'How would you rate the value for money of our products/services?',
      'singleq',
      ['Bad','Average','Good']
    )
  ]
}

function improveQuestions () {
  return [
  genQuestion(
    'What would you change about your experience?',
    'openq',
    [],
    'Alternative: If you could change just one thing about us, what would it be?'
  ),
  genQuestion(
    'Is there anything you were expecting that you didn’t get?'
  ),
  genQuestion(
    'Which, if any, of our services/products aren\'t useful to you?'
  ),
  genQuestion(
    'How could we improve our product/service?', //text
  ),
  genQuestion(
    'What do you like least about our product/service?', //text
  ),
  genQuestion(
    'How can we improve your experience with _businessname_?'
  )]
}


function responsivenessQuestions () {
  return [
  genQuestion(
    'How responsive have we been to your questions or concerns about our products?', //text
    'singleq', //type 'multiq'/'openq'/'singleq'
    ['I don\'t know yet', 'Very responsive', 'Usually responsive', 'Not responsive'] //answer options
  ),
  genQuestion(
    'On a scale of ‘very easy’ to ‘very difficult’, how easy was it to interact with _businessname_? What made your experience like that?'
  )]
}


function genQuestion(txt, typ, opts, d) {

  if (typeof typ === "undefined") typ = 'openq'
  if (typ === 'openq') opts = []
  if (typeof d === "undefined") d = null

  let answerOptions = []

  //console.log(txt)
  opts.forEach((item, i) => {
    answerOptions.push(
      { text: item }
    )
  });


  return {
    questionText: txt,
    questionType: typ,
    description: d,
    answerOptions: answerOptions
  }

}


/*

genQuestion(
  '', //text
  '', //type 'multiq'/'openq'/'singleq'
  [], //answer options
  '' //description
)

*/
